/**
 * RenderErrorHandler - Inline error rendering for canvas fallback
 *
 * Extracted from EdgeDocsCanvas.V3.tsx (lines 1809-1850) as part of Phase 3 Step 2.
 *
 * This module handles inline rendering of error messages directly on the canvas
 * when WASM text rendering fails. It provides:
 * - Circuit breaker visualization (red error screen when max errors reached)
 * - Fallback text preview for debugging
 * - Visual error feedback for users
 *
 * NOTE: This is different from WasmErrorRecovery which handles recovery logic.
 * This module focuses purely on VISUAL ERROR RENDERING on the canvas.
 */

/**
 * Circuit breaker threshold for consecutive WASM errors
 */
export const MAX_CONSECUTIVE_WASM_ERRORS = 3;

/**
 * Parameters for rendering error states on canvas
 */
export interface RenderErrorParams {
  /** Canvas 2D rendering context to draw on */
  ctx: CanvasRenderingContext2D;
  /** The error that occurred */
  error: Error;
  /** Current count of consecutive WASM errors */
  consecutiveErrors: number;
  /** Optional text to show in fallback preview */
  renderText?: string;
  /** Total document text length for context */
  documentTextLength?: number;
}

/**
 * Renders circuit breaker error screen (red crash screen)
 *
 * Called when consecutive errors exceed MAX_CONSECUTIVE_WASM_ERRORS.
 * Displays a clear error message instructing user to reload.
 *
 * @param params - Error rendering parameters
 */
export function renderCircuitBreakerError(params: RenderErrorParams): void {
  const { ctx, error, consecutiveErrors } = params;

  console.error(
    `🔥 [CanvasV3] CIRCUIT BREAKER TRIGGERED: ${consecutiveErrors} consecutive WASM errors - stopping re-renders`
  );

  // Render error message
  ctx.save();
  ctx.resetTransform();
  ctx.fillStyle = '#FF0000';
  ctx.font = 'bold 18px Arial';
  ctx.fillText('⚠️ WASM ENGINE CRASHED', 20, 50);
  ctx.font = '14px Arial';
  ctx.fillStyle = '#000000';
  ctx.fillText('The document is too large or contains invalid data.', 20, 80);
  ctx.fillText('Please reload the page to recover.', 20, 110);
  ctx.fillText(`Error: ${error.message}`, 20, 140);
  ctx.restore();
}

/**
 * Renders fallback text preview for debugging
 *
 * Called when WASM rendering fails but circuit breaker hasn't triggered yet.
 * Shows red "FALLBACK TEXT RENDERING" with text preview for debugging.
 *
 * @param params - Error rendering parameters
 */
export function renderFallbackTextPreview(params: RenderErrorParams): void {
  const { ctx, error, renderText = '', documentTextLength = 0 } = params;

  // Fallback: render simple text for debugging - make it very visible
  ctx.save();
  ctx.resetTransform(); // Reset any scaling for fallback text
  ctx.fillStyle = '#FF0000'; // Red text to make it obvious
  ctx.font = '16px Arial';
  ctx.fillText('FALLBACK TEXT RENDERING:', 20, 50);
  ctx.fillStyle = '#000000';
  ctx.font = '14px Arial';
  const previewText = renderText.substring(0, 50);
  ctx.fillText(`"${previewText}"`, 20, 80);
  ctx.fillText(`Rendering ${renderText.length} chars (from ${documentTextLength} total)`, 20, 110);
  ctx.fillText(`WASM Error: ${error.message}`, 20, 140);
  ctx.restore();

  console.log('🎨 [CanvasV3] Rendered fallback text in red');
}

/**
 * Handles WASM rendering errors with appropriate visual fallback
 *
 * This is the main entry point for rendering errors. It decides whether to
 * show the circuit breaker screen or fallback text preview based on error count.
 *
 * @param params - Error rendering parameters
 * @returns true if circuit breaker triggered (should stop further rendering)
 */
export function handleRenderError(params: RenderErrorParams): boolean {
  console.error('🎨 [CanvasV3] WASM text rendering failed:', params.error);

  // Check if circuit breaker should trigger
  if (params.consecutiveErrors >= MAX_CONSECUTIVE_WASM_ERRORS) {
    renderCircuitBreakerError(params);
    return true; // Signal that rendering should stop
  }

  // Otherwise, render fallback text preview
  renderFallbackTextPreview(params);
  return false; // Rendering can continue
}
